import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { keepLastResolvedValue } from '@okcargo-apps/layout-ionic';
import { IBatchImport } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import {
  BatchImportQueryService,
  ConnectivityService,
  PartialSearchOptions,
} from '@okcargo-apps/shared-infrastructure';
import { FindBatchImportRowsQuery } from '@okcargo/query-processor';
import { AccountService } from '../account';
import { IBatchImportRowsVM } from './batch-import-errors-store.model';

@Injectable({
  providedIn: 'root',
})
export class BatchImportStoreService {
  private readonly batchImportQueryService = inject(BatchImportQueryService);
  private readonly accountService = inject(AccountService);
  private readonly connectivity = inject(ConnectivityService);

  private readonly PAGE_INDEX = 0;
  private readonly PAGE_SIZE = 10000;

  private readonly _selectedBatchImportId = signal<string | null>(null);

  public readonly batchImports = resource({
    params: () => {
      const canView =
        this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER) ||
        this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER);

      return canView ? { isValid: true } : null;
    },
    loader: async ({ params, abortSignal }): Promise<IBatchImport[]> => {
      if (!params) return [];

      const options: PartialSearchOptions = {
        criteriaPayload: {
          pageIndex: this.PAGE_INDEX,
          pageSize: this.PAGE_SIZE,
          order: [],
        },
        abortSignal,
      };

      return await this.batchImportQueryService.searchBatchImports(options);
    },
  });

  public reloadBatchImports(): void {
    if (this.connectivity.isOffline()) return;
    this.batchImports.reload();
  }

  public reloadAfterSaveData(): void {
    setTimeout(() => {
      this.reloadBatchImports();
    }, 2000); // 2 sec
  }

  private readonly currentBatchImport = resource({
    params: () => this._selectedBatchImportId(),
    loader: async ({ params: id, abortSignal }) => {
      if (!id) return null;

      const query: FindBatchImportRowsQuery = {
        batchImportId: id,
      };

      const response = await this.batchImportQueryService.findBatchImportRows(query);
      return response;
    },
  });

  /** True when an action that needs the backend cannot be performed (no connectivity). */
  public readonly actionsDisabled = computed(() => this.connectivity.isOffline());

  /*
   * Keeps the last loaded rows when a reload fails (e.g. the connection drops in
   * the middle of a refresh) instead of blanking the view. The selected id is
   * used as the request key, so rows of a previous import are never shown as if
   * they were the selected one.
   */
  private readonly _lastResolvedCurrentBatchImport = keepLastResolvedValue(
    this.currentBatchImport.snapshot,
    this._selectedBatchImportId,
  );

  public readonly currentBatchImportVM = computed(() => {
    const error = this.currentBatchImport.error();

    const vm: IBatchImportRowsVM = {
      item: this._lastResolvedCurrentBatchImport() ?? null,
      isLoading: this.currentBatchImport.isLoading(),
      error: error as Error | undefined,
    };

    return vm;
  });

  public setSelectedBatchImportId(id: string | null): void {
    this._selectedBatchImportId.set(id);
  }

  // not in use for now
  public clearSelectedBatchImportId(): void {
    this._selectedBatchImportId.set(null);
  }

  public reloadCurrentBatchImport(): void {
    if (this.connectivity.isOffline()) return;
    this.currentBatchImport.reload();
  }
}
